<template>
  <UnsavedChangesModal ref="unsavedChangesModal" />

  <div class="d-flex flex-row mx-2 mt-2">
    <table class="table table-borderless table-sm align-self-start">
      <tbody>
      <tr>
        <td colspan="2" class="pb-4">
          <div class="d-flex justify-content-between">
            <div class="btn-group">
              <RouterLink :to="{ name: 'items' }" class="btn btn-outline-secondary">
                <font-awesome-icon :icon="['fas', 'chevron-left']" />
                <span class="d-none d-sm-inline-block ms-1">{{ $t('global.list') }}</span>
              </RouterLink>
              <RouterLink v-if="isEditMode" :to="{ name: 'stocks', params: { itemId: itemId }}" :title="$t('menu.stocks')" class="btn btn-outline-secondary">
                <font-awesome-icon :icon="['fas', 'boxes-stacked']" />
              </RouterLink>
              <RouterLink v-if="isEditMode" :to="{ name: 'stock-change', params: { itemId: itemId } }" :title="$t('menu.stock-changes')" class="btn btn-outline-secondary">
                <font-awesome-icon :icon="['fas', 'plus-minus']" />
              </RouterLink>
              <AddToOrderDraft v-if="isEditMode" :item-id="itemId" :restock-pull-type="effectiveRestockPullType" :can-order-manually="itemSiteSetting?.canOrderManually ?? false" />
            </div>
            <div class="flex-grow-1 ms-3">
              <button class="btn btn-primary w-100" type="submit" @click="saveAll">
                <font-awesome-icon :icon="['fas', 'floppy-disk']" />
                {{ isEditMode ? $t('action.save') : $t('action.create') }}
              </button>
            </div>
          </div>
        </td>
      </tr>
      <tr>
        <td>
          <label class="fw-semibold" for="code">{{ $t('field.masterdata.itemCode') }}</label>
        </td>
        <td>
          <div class="input-group">
            <input :disabled="isEditMode || !form.isInternalGenerated" v-model="form.code" type="text" :class="isInvalidItemCode ? 'form-control invalid was-validated' : 'form-control'" required maxlength="20" size="25" id="code" ref="code" aria-describedby="codeHelp" @input="convertToUpperCase">
            <button v-if="!form.isInternalGenerated" class="btn btn-outline-secondary" type="button" @click="copyKey(form.code)">
              <font-awesome-icon :icon="['fas', 'copy']" />
            </button>
          </div>
        </td>
      </tr>
      <tr>
        <td>
          <label class="fw-semibold" for="name">{{ $t('field.generic.name') }}</label>
        </td>
        <td>
          <input v-model="form.name" :disabled="!form.isInternalGenerated" type="text" class="form-control" size="25" id="name" ref="name" aria-describedby="nameHelp" required maxlength="100">
        </td>
      </tr>
      <tr>
        <td>
          <label class="fw-semibold" for="description">{{ $t('page.item.form.description') }}</label>
        </td>
        <td>
          <input v-model="form.description" :disabled="!form.isInternalGenerated"  type="text" class="form-control" size="25" id="description" ref="description" aria-describedby="descriptionHelp" >
        </td>
      </tr>
      <tr>
        <td>
          <label class="fw-semibold" for="baseUnitCode">{{ $t('field.item.unitName') }}</label>
        </td>
        <td>
          <input v-model="form.baseUnitCode" :disabled="!form.isInternalGenerated" type="text" class="form-control" size="10" id="baseUnitCode" ref="baseUnitCode" aria-describedby="nameHelp" required minlength="1" maxlength="10" @input="convertBaseUnitToUpperCase">
        </td>
      </tr>
      <tr>
        <td class="px-3">
          <input type="checkbox" v-model="form.trackLotNumber" :disabled="!form.isInternalGenerated" id="trackLotNumber" class="form-check-input" />
          <label for="trackLotNumber" class="form-check-label px-1">
            <font-awesome-layers class="me-1">
              <font-awesome-icon :icon="['far', 'rectangle-wide']" />
              <font-awesome-layers-text value="LOT" transform="shrink-8" />
            </font-awesome-layers>
            {{ $t('page.item.table.trackLotNumber') }}
          </label>
        </td>
        <td v-if="form.trackLotNumber" class="px-3">
          <input type="checkbox" v-model="form.allowCalculatedLot" id="allowCalculatedLot" class="form-check-input" />
          <label for="allowCalculatedLot" class="form-check-label px-1">
            <font-awesome-layers class="text-melodi">
              <font-awesome-icon :icon="['far', 'rectangle-wide']" />
              <font-awesome-layers-text value="AUTO LOT" transform="shrink-8 up-6" />
            </font-awesome-layers>
            {{ $t('page.item.table.allowCalculateLot') }}
          </label>
        </td>
      </tr>
      <tr>
        <td colspan="2" class="px-3">
          <input type="checkbox" v-model="form.trackExpirationDate" :disabled="!form.isInternalGenerated" id="trackExpirationDate" class="form-check-input" />
          <label for="trackExpirationDate" class="form-check-label px-1">
            <font-awesome-icon :icon="['fas', 'hourglass-end']" class="me-1 text-secondary" />
            {{ $t('page.item.table.trackExpirationDate') }}
          </label>
        </td>
      </tr>
      <tr>
        <td colspan="2" class="px-3">
          <input type="checkbox" v-model="form.isBillable" id="isBillable" class="form-check-input" />
          <label for="isBillable" class="form-check-label px-1">
            <font-awesome-icon :icon="['fas', 'coins']" class="me-1 text-secondary" />
            {{ $t('page.item.table.isBillable') }}
          </label>
        </td>
      </tr>
      <tr v-if="form.trackBestBeforeDate">
        <td>
          <label for="bestBeforeLeadTime" class="fw-semibold">{{ $t('page.item.table.bestBeforeLeadTime') }}</label>
        </td>
        <td>
          <input type="number" v-model="form.bestBeforeLeadTime" :disabled="!form.isInternalGenerated" id="bestBeforeLeadTime" class="form-control" />
        </td>
      </tr>
      <tr v-if="form.trackExpirationDate">
        <td>
          <label for="expirationLeadTime" class="fw-semibold">{{ $t('page.item.table.expirationLeadTime') }}</label>
        </td>
        <td>
          <input type="number" v-model="form.expirationLeadTime" :disabled="!form.isInternalGenerated" id="expirationLeadTime" class="form-control" />
        </td>
      </tr>
      <tr v-if="selectedSite && !form.isInternalGenerated">
        <td colspan="2">
          <div class="mel-divider d-flex align-items-center my-3">
            <div class="text-primary mx-3">
              <font-awesome-layers class="fa-1x me-2">
                <font-awesome-icon :icon="['fas', 'warehouse']" />
              </font-awesome-layers>
              <span class="ms-1">{{ selectedSite.tenant.name }} - {{ selectedSite.name }}</span>
            </div>
          </div>
        </td>
      </tr>
      <tr v-if="selectedSite && !form.isInternalGenerated">
        <td>
          <label class="fw-semibold" for="defaultLabelQuantity">{{ $t('page.item.form.defaultLabelQuantity') }}</label>
        </td>
        <td>
          <input v-model="itemSiteSetting.defaultLabelQuantity" type="number" min="0" max="1000" class="form-control" size="25" id="defaultLabelQuantity" ref="defaultLabelQuantity" aria-describedby="defaultLabelQuantityHelp">
        </td>
      </tr>
      <tr v-if="selectedSite && !form.isInternalGenerated">
        <td>
          <label class="fw-semibold">{{ $t('page.item.form.defaultLabelTemplate') }}</label>
        </td>
        <td>
          <LabelTemplateSelector :predefined-template-id="preselectedTemplateId" @template-selected="assignTemplate" :allow-empty="true"/>
        </td>
      </tr>
      <tr v-if="selectedSite && !form.isInternalGenerated">
        <td>
          <label class="fw-semibold">{{ $t('page.item.form.restockPullType') }}</label>
        </td>
        <td>
          <RestockPullTypeSelector :predefined-pull-type="itemSiteSetting?.restockPullType ?? RESTOCK_PULL_TYPE.onDemand" @pull-type-selected="(pullType: RestockPullType) => itemSiteSetting.restockPullType = pullType"/>
        </td>
      </tr>
      <tr v-if="selectedSite && !form.isInternalGenerated">
        <td colspan="2" class="px-3">
          <input type="checkbox" v-model="itemSiteSetting.canOrderManually" id="canOrderManually" class="form-check-input" />
          <label for="canOrderManually" class="form-check-label px-1">
            <font-awesome-icon :icon="['fas', 'cart-plus']" class="me-1 text-secondary" />
            {{ $t('page.item.table.canOrderManually') }}
          </label>
        </td>
      </tr>
      </tbody>
    </table>

    <div v-if="isEditMode" class="d-flex flex-column" style="min-width: 500px;">
      <div class="mel-divider d-flex align-items-center my-3">
        <div class="text-primary mx-3">
          {{ $t('field.generic.tradeItem', 0) }}
        </div>
      </div>

      <div>
        <table id="gtins" class="table table-borderless table-sm align-middle mb-0">
          <thead>
            <tr>
              <th>{{ $t('field.generic.code') }}</th>
              <th>{{ $t('field.tradeItem.unitCode') }}</th>
              <th>{{ $t('field.tradeItem.factor') }}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(tradeItem, index) in tradeItems" :key="index">
              <td v-if="form.isInternalGenerated" ><ScanInput name="tradeItemRowCode" :gs1-field-selector="(barcode, gs1) => gs1?.gtin ?? barcode" :disabled="!form.isInternalGenerated" v-model="tradeItem.code" required /></td>
              <td v-else><input type="text" name="tradeItemRowCode" :disabled="!form.isInternalGenerated" v-model="tradeItem.code" class="form-control" required maxlength="14" size="15"></td>
              <td><input type="text" :disabled="!form.isInternalGenerated" v-model="tradeItem.unitCode" class="form-control" required maxlength="5" size="5" @input="(event) => convertUnitCodeToUpperCase(event, index)"></td>
              <td>
                <div class="d-inline-block">
                  <input v-if="tradeItem.unitCode !== form.baseUnitCode" type="number" :disabled="!form.isInternalGenerated" v-model="tradeItem.factor" class="form-control" min="1" max="9999" size="5" required>
                </div>
                <div class="d-inline-block">&nbsp;<sup v-if="tradeItem.unitCode !== form.baseUnitCode">{{ form.baseUnitCode }}</sup></div>
              </td>
              <td>
                <button v-if="form.isInternalGenerated" class="btn btn-outline-danger" type="button" @click="deleteTradeItem(tradeItem.code)">
                  <font-awesome-icon :icon="['fas', 'trash']" />
                </button>
              </td>
            </tr>
          </tbody>
        </table>

        <button v-if="form.isInternalGenerated && allTradeItemCodesFilled" type="button" class="btn btn-link" @click="addNewTradeItem()">
          <font-awesome-icon :icon="['fas', 'plus']" /> {{ $t('action.addEllipsis') }}
        </button>
      </div>

      <div class="mel-divider d-flex align-items-center my-3">
        <div class="text-primary mx-3">
          {{ $t('global.picture', 0) }}
        </div>
      </div>

      <div v-for="imageType in imageTypes" :key="imageType" class="px-2 d-flex flex-column mb-5" style="min-height: 144px; max-height: 256px; min-width: 384px;">
        <span class="fw-semibold">{{ $t(`page.item.form.${imageType}Image`) }}</span>
        <FileDrop class="border border-tertiary border-3 rounded-3 d-flex flex-grow-1 align-items-center mt-3" style="max-width: 384px; min-height: 96px; max-height: 224px;"
                  :initial-image="images[imageType]?.binaryContent"
                  :initial-image-type="images[imageType]?.imageDataType"
                  @image-changed="(file: File | undefined) => savePicture(file, imageType)">
        </FileDrop>
      </div>

      <OpenOrdersForItem :item-id="props.itemId" />
    </div>
  </div>
</template>

<script setup lang="ts">

import { computed, nextTick, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import { storeToRefs } from "pinia";
import { useI18n } from "vue-i18n";
import { toast, type ToastOptions } from "vue3-toastify";
import {
  useItemSiteSettingsStore,
  useItemsStore,
  useSessionStore,
  useItemImagesStore,
  useTradeItemsStore
} from "@/stores";
import FileDrop from "@/components/form/FileDrop.vue";
import LabelTemplateSelector from "@/components/labels/LabelTemplateSelector.vue";
import RestockPullTypeSelector from "@/components/enumSelectors/RestockPullTypeSelector.vue";
import ItemSiteSetting from "@/models/frontend/ItemSiteSetting";
import LabelTemplate from "@/models/frontend/LabelTemplate";
import ItemImage from "@/models/frontend/ItemImage";
import IItem from "@/models/responses/IItem.ts";
import { RESTOCK_PULL_TYPE, RestockPullType } from "@/models/Enums.ts";
import { UpdateItemRequest } from "@/models/requests/UpdateItemRequest.ts";
import { CreateItemRequest } from "@/models/requests/CreateItemRequest.ts";
import type ITradeItem from "@/models/responses/ITradeItem.ts";
import ScanInput from "@/components/form/ScanInput.vue";
import OpenOrdersForItem from "@/components/orders/OpenOrdersForItem.vue";
import AddToOrderDraft from "@/components/orders/AddToOrderDraft.vue";
import UnsavedChangesModal from "@/components/UnsavedChangesModal.vue";
import { useUnsavedChangesGuard } from "@/services/unsavedChangesGuard";

const props = defineProps({
  itemId: { type: Number, required: false, default: 0 }
});

const i18n = useI18n();
const router = useRouter();

const itemStore = useItemsStore();
const tradeItemsStore = useTradeItemsStore();
const itemImageStore = useItemImagesStore();
const itemSiteSettingStore = useItemSiteSettingsStore();
const sessionStore = useSessionStore();

const isEditMode = computed(() => {
  return props.itemId != 0;
});

const form = ref({
  id: 0,
  tenantId: 0,
  code: "",
  name: "",
  description: "",
  annotation: "",
  trackLotNumber: false,
  trackBestBeforeDate: false,
  trackExpirationDate: false,
  bestBeforeLeadTime: 0,
  expirationLeadTime: 0,
  requiresLabel: false,
  baseUnitCode: "",
  isInternalGenerated: true,
  isBillable: true,
  allowCalculatedLot: false
} as IItem);

const isInvalidItemCode = computed(() => {
  return !isEditMode.value && itemStore.data.find(x => x.code == form.value.code);
});

const { error: itemImageStoreError } = storeToRefs(itemImageStore);
const { error: itemSiteSettingStoreError } = storeToRefs(itemSiteSettingStore);
const { selectedSite } = storeToRefs(sessionStore);

const imageTypes = ['main', 'size', 'sizeMini'];
const images = ref<{ [imageType: string] : ItemImage | undefined; }>({
  'main': undefined,
  'size': undefined,
  'sizeMini': undefined
});

const tradeItems = ref<ITradeItem[]>([]);

let uploads: { [imageType: string] : File | undefined; }  = {}

const itemSiteSetting = ref<ItemSiteSetting>({} as ItemSiteSetting);
const error = ref('');

const savedSnapshot = ref('');
const snapshotInitialized = ref(false);
const picturesDirty = ref(false);
const unsavedChangesModal = ref<InstanceType<typeof UnsavedChangesModal> | null>(null);

const computeSnapshot = () => JSON.stringify({
  form: form.value,
  tradeItems: tradeItems.value,
  itemSiteSetting: itemSiteSetting.value,
});

const hasUnsavedChanges = computed(() => {
  if (!snapshotInitialized.value) return false;
  return picturesDirty.value || computeSnapshot() !== savedSnapshot.value;
});

const captureSavedSnapshot = () => {
  savedSnapshot.value = computeSnapshot();
  picturesDirty.value = false;
  snapshotInitialized.value = true;
};

useUnsavedChangesGuard(() => hasUnsavedChanges.value, unsavedChangesModal);

const effectiveRestockPullType = computed(() => {
  return itemSiteSetting.value?.restockPullType ?? form.value.restockPullType ?? RESTOCK_PULL_TYPE.onDemand;
});

const preselectedTemplateId = computed(() => {
  return itemSiteSetting.value?.defaultLabelTemplate?.id ?? 0;
});

const allTradeItemCodesFilled = computed(() => {
  return tradeItems.value.every(x => x.code != undefined && x.code.trim() != '');
});

const savePicture = async (file: File | undefined, imageTypeCode: string) => {
  uploads[imageTypeCode] = file;
  picturesDirty.value = true;
}

const assignTemplate = async(template: LabelTemplate | undefined) => {
  itemSiteSetting.value.defaultLabelTemplate = template;
}

const saveAll = async () => {
  error.value = "";

  const tenant = sessionStore.selectedTenant;
  if (!tenant) {
    console.error("Tenant not selected");
    return;
  }

  let itemId = 0;
  if (isEditMode.value) {
    itemId = props.itemId;
    const updateRequest = {
      ...form.value,
      id: props.itemId,
      tenantCode: tenant.code,
    } as UpdateItemRequest;
    await itemStore.update(props.itemId, updateRequest);
  } else {
    const createRequest = {
      ...form.value,
      tenantCode: tenant.code,
    } as CreateItemRequest;
    itemId = await itemStore.create(createRequest);
  }

  const item = itemStore.getById(itemId);

  if (itemStore.error || !item) {
    toast.error(itemStore.error, { theme: 'colored', autoClose: 2000 } as ToastOptions);
    return;
  }

  const tradeItemsToSync = tradeItems.value.filter(x => x.code != undefined && x.code.trim() != '');
  for (const tradeItem of tradeItemsToSync) {
    if (tradeItem.unitCode === form.value.baseUnitCode) {
      tradeItem.factor = 1; // ensure that the factor for the base unit is always 1
    }
    tradeItem.itemId = item.id;
  }

  if (item.isInternalGenerated) {
    await tradeItemsStore.sync(item.id, tradeItemsToSync);
    if (tradeItemsStore.error) {
      toast.error(i18n.t('page.item.saveError'), { theme: 'colored', autoClose: 2000 } as ToastOptions);
      return;
    }
  }

  await itemSiteSettingStore.updateItemSiteSettings(item, selectedSite.value!, itemSiteSetting.value);
  if (itemSiteSettingStoreError.value) {
    toast.error(i18n.t('page.item.saveError'), { theme: 'colored', autoClose: 2000 } as ToastOptions);
    return;
  }

  for (const imageType in uploads) {
    const targetImage = uploads[imageType];
    if (images.value[imageType] != undefined && targetImage == undefined) {
      await itemImageStore.deleteItemImage(images.value[imageType]!.id);
    } else {
      await itemImageStore.saveItemImage(item.id!, imageType, targetImage!);
    }

    if (itemImageStoreError.value) {
      toast.error(i18n.t('page.item.saveError'), { theme: 'colored', autoClose: 2000 } as ToastOptions);
      return;
    }
  }

  toast.info(i18n.t('page.item.saved'), { theme: 'colored', autoClose: 800 } as ToastOptions);
  captureSavedSnapshot();

  if (!isEditMode.value) {
    //reroute so we are in the normal edit mode of the newly created item
    router.push({ name: "item", params: { itemId: item.id } });
  }
}

const addNewTradeItem = async () => {
  tradeItems.value.push({
    code: '',
    unitCode: i18n.t('field.tradeItem.defaultUnitCode'),
    factor: 1,
    itemId: props.itemId ?? 0,
    id: 0
  } as ITradeItem);

  // next tick:
  // wait for the DOM to update before focusing the input
  await nextTick(() => {
    focusLastTradeItemInput();
  });
}

const deleteTradeItem = (code: string) => {
  tradeItems.value = tradeItems.value.filter(x => x.code != code);
  if (tradeItems.value.length == 0) {
    addNewTradeItem()
  }
}

const convertToUpperCase = (event: Event) => {
  const input = event.target as HTMLInputElement;
  input.value = input.value.toUpperCase();
  form.value.code = input.value;
}

const convertUnitCodeToUpperCase = (event: Event, index: number) => {
  const input = event.target as HTMLInputElement;
  input.value = input.value.toUpperCase();
  tradeItems.value[index]!.unitCode = input.value;
}

const convertBaseUnitToUpperCase = (event: Event) => {
  const input = event.target as HTMLInputElement;
  input.value = input.value.toUpperCase();
  form.value.baseUnitCode = input.value;
}

const refresh = async () => {
  itemSiteSetting.value = itemSiteSettingStore.getByItemId(props.itemId!) ?? {} as ItemSiteSetting;
  for (const imageType of imageTypes) {
    images.value[imageType] = await itemImageStore.loadItemImageByType(props.itemId!, imageType);
  }
  uploads = {};
}

onMounted(async () => {
  tradeItems.value = tradeItemsStore.data
    .sort((a, b) => a.factor - b.factor || a.unitCode.localeCompare(b.unitCode))
    .filter((item: ITradeItem) => item.itemId == props.itemId);
  
  if (tradeItems.value.length == 0) {
    addNewTradeItem()
  }
  
  if (isEditMode.value) {
    const currentItem = itemStore.getById(props.itemId!);
    if (!currentItem) {
      error.value = `invalid item ${props.itemId}`;
      console.error("Item not found");
      return;
    }

    error.value = '';
    form.value = { ...form.value, ...currentItem };

    await refresh();
  }

  captureSavedSnapshot();
})

function focusLastTradeItemInput() {
  // select the first text input field of the last added trade item row
  const table = document.querySelector('#gtins') as HTMLTableElement;
  if (!table) { return; }
  const lastRow = table.querySelector('tbody tr:last-child');
  if (!lastRow) { return; }
  const inputElement = lastRow.querySelector('input[type="text"]') as HTMLInputElement;
  if (!inputElement) { return; }
  if (inputElement) {
    inputElement.focus();
    inputElement.select();
  }
}

const copyKey = (itemCode: string) => {
  copyToClipboardWithMessage(itemCode, i18n.t('statement.itemCopied', { item: itemCode }));
}

function copyToClipboardWithMessage(clipboardContent: string, message: string) {
  navigator.clipboard.writeText(clipboardContent).then(
    () => toast.success(message, { autoClose: 1000 }),
    () => toast.error(i18n.t('error.copiedError'), { autoClose: 1000 })
  );
}
</script>
